import { useMemo, useState } from "react";
import {
    AccountingLedgerBasis,
    BOTH_LEDGER_BASIS,
    LedgerBasisInput,
} from "@common/types/domains/accounting/ledger";
import { keyBy } from "@common/helpers/collections";
import { useManualJournalAccounts } from "@hooks/useManualJournalAccounts";

interface UseBothLedgersAccountsParams {
    entityId: number;
    ledgerBasis?: AccountingLedgerBasis;
}

export function useBothLedgersAccounts({
    entityId,
    ledgerBasis,
}: UseBothLedgersAccountsParams) {
    const [formLedgerBasis, setFormLedgerBasis] = useState<
        LedgerBasisInput | undefined
    >(ledgerBasis);
    const [prevLedgerBasis, setPrevLedgerBasis] = useState(ledgerBasis);
    if (ledgerBasis !== prevLedgerBasis) {
        setPrevLedgerBasis(ledgerBasis);
        setFormLedgerBasis(ledgerBasis);
    }

    const isBothLedgers = formLedgerBasis === BOTH_LEDGER_BASIS;
    const effectiveLedgerBasis = isBothLedgers
        ? AccountingLedgerBasis.cash
        : formLedgerBasis;

    const { accounts: primaryAccounts, accountsById: primaryAccountsById } =
        useManualJournalAccounts(entityId, effectiveLedgerBasis);

    const { accounts: accrualAccounts, accountsById: accrualAccountsById } =
        useManualJournalAccounts(
            isBothLedgers ? entityId : undefined,
            AccountingLedgerBasis.accruals,
        );

    // Cash and accrual ledgers share entity-scoped account rows.
    const allowedAccountIds = useMemo(() => {
        if (!isBothLedgers) {
            return undefined;
        }
        return new Set(accrualAccounts.map((a) => a.id));
    }, [isBothLedgers, accrualAccounts]);

    const accounts = useMemo(() => {
        if (!isBothLedgers) {
            return primaryAccounts;
        }
        return primaryAccounts.filter((a) => accrualAccountsById[a.id]);
    }, [isBothLedgers, primaryAccounts, accrualAccountsById]);

    const accountsById = useMemo(() => {
        if (!isBothLedgers) {
            return primaryAccountsById;
        }
        return keyBy(accounts, (a) => a.id);
    }, [isBothLedgers, primaryAccountsById, accounts]);

    return {
        formLedgerBasis,
        setFormLedgerBasis,
        isBothLedgers,
        effectiveLedgerBasis,
        accounts,
        accountsById,
        allowedAccountIds,
    };
}
